import { IosShare, WarningOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import React, { Fragment } from 'react'
import { ResultCardData } from '.'
import { colors } from '../../constants/colors'
import { locationState } from '../../context/location-context'
import { HBox, InfoBox, VBox } from '../../elements/basic-elements'
import { ErrorText, TextButton } from '../../elements/form-elements'
import { BallotReadyRepresentativesObject } from '../../types/ballot-ready-types'
import { AddressComponent } from '../../types/civic-info-types'
import { LocationResponse } from '../../types/location-types'
import { getDomain } from '../../utilities/browser'
import { addSentenceStructureToStringArray, slugify } from '../../utilities/string'
import AddressInput from '../address-input'
import PrimaryButton from '../buttons/primary-button'
import { ShareButton } from '../share-button'
import { RepCardData } from './rep-card'
import RepresentativeButton from './representative-button'

const Representatives = ({
  action,
  onSelectRep
}: {
  action: ResultCardData
  onSelectRep: (data: RepCardData) => void
}) => {
  const { outreach, location, shareUrl, slug } = action
  const {
    locationLoading,
    settings,
    locationData,
    representativesData,
    representativesError,
    representativesLoading,
    homeLocation,
    setSettings
  } = locationState()
  const { address } = settings
  const sameAddress = (a?: string, b?: string) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()
  // Representatives are for the user's My Location: offer it when another
  // address is entered.
  const showUseHome = Boolean(homeLocation && !sameAddress(address, homeLocation.locationText))

  const { targets, locationText, targetsOther } = outreach
  const targetsAreOnlyOther = targets.every(target => target === 'other')

  const representatives = targets
    ?.map(representative => {
      const repOfType =
        representative &&
        representativesData &&
        representativesData[representative as keyof BallotReadyRepresentativesObject]
      return repOfType
    })
    .filter(repOfType => repOfType !== undefined) as BallotReadyRepresentativesObject[]

  const fewerRepsThanExpected =
    representativesData &&
    targets &&
    representatives &&
    representatives.length < [...targets].splice(targets.indexOf('other'), 1).length

  const locationMismatch = representativesData && getLocationMismatch(location, locationData)

  const theme = useTheme()
  const background = theme.palette.mode === 'dark' ? colors.DARK_MODE_70 : colors.BLACK_10
  return (
    <VBox gap={2} my={4}>
      <Typography variant="body1" color="textPrimary">
        Be Heard! Contact Your Representatives:
      </Typography>
      {!targetsAreOnlyOther && (
        <Fragment>
          <Box sx={{ pointerEvents: 'all' }}>
            <AddressInput background={background} />
            {showUseHome && homeLocation && (
              <TextButton
                sx={{ mt: 0.5, p: 0, textDecoration: 'underline' }}
                onClick={() => setSettings({ ...settings, address: homeLocation.locationText })}
              >
                <Typography variant="caption" color="textPrimary">
                  Use My Location
                </Typography>
              </TextButton>
            )}
            {!locationMismatch && (!address || fewerRepsThanExpected) && (
              <Typography variant="body2" color="textSecondary" mt={0.5}>
                <HBox>
                  <Box flex={0} mr={0.5}>
                    *
                  </Box>
                  <Box>
                    {!address && 'Add your location to find your local representatives. '}
                    {locationText ||
                      'Please include your city, state and zipcode for the most accurate results.'}
                  </Box>
                </HBox>
              </Typography>
            )}
          </Box>

          {locationMismatch && (
            <InfoBox>
              <VBox gap={1}>
                <Typography color="textPrimary" variant="body1">
                  Looks like you don't live in {locationMismatch}. Share this with someone who does!
                </Typography>
                <ShareButton text={shareUrl || getDomain() + '/action/' + slug?.current}>
                  {handleCopy => (
                    <PrimaryButton
                      fullWidth
                      variant="contained"
                      endIcon={<IosShare />}
                      onClick={handleCopy}
                    >
                      Share This Action
                    </PrimaryButton>
                  )}
                </ShareButton>
              </VBox>
            </InfoBox>
          )}
          {!locationMismatch && !fewerRepsThanExpected && targets && targets.length > 0 && (
            <Fragment>
              <Box>
                {representativesLoading || locationLoading ? (
                  <CircularProgress color="secondary" />
                ) : (
                  <VBox gap={1}>
                    {representativesError ? (
                      <ErrorText>{representativesError}</ErrorText>
                    ) : (
                      representatives.map(representative => {
                        const { office, representatives } = representative
                        return (
                          <VBox gap={1}>
                            {representatives?.map(rep => {
                              const party = rep.parties[0]
                              return (
                                <RepresentativeButton
                                  name={`${rep.person.fullName}`}
                                  title={`${party ? party?.name + ', ' : ''}${office}`}
                                  photo={rep.person.headshot.defaultUrl}
                                  onClick={() => {
                                    onSelectRep({
                                      name: rep.person.fullName,
                                      party: party?.name,
                                      photo: rep.person.headshot.defaultUrl,
                                      websites: rep.urls,
                                      title: office,
                                      phones: [
                                        rep.centralPhone
                                          ? { type: 'Primary Phone', phone: rep.centralPhone }
                                          : undefined,
                                        ...rep.person.contacts.map(({ phone, type }) => ({
                                          type,
                                          phone
                                        }))
                                      ]
                                        .filter(phone => !!phone)
                                        .reduce((phones, phone) => {
                                          if (
                                            !phones.find(existing => existing.phone !== phone.phone)
                                          ) {
                                            phones.push(phone)
                                          }
                                          return phones
                                        }, []),

                                      emails: [
                                        rep.primaryEmail
                                          ? { type: 'Primary Email', email: rep.primaryEmail }
                                          : undefined,
                                        ...rep.person.contacts.map(({ email, type }) => ({
                                          type,
                                          email
                                        }))
                                      ]
                                        .filter(email => !!email)
                                        .reduce((emails, email) => {
                                          if (
                                            !emails.find(existing => existing.email !== email.email)
                                          ) {
                                            emails.push(email)
                                          }
                                          return emails
                                        }, [])
                                    })
                                  }}
                                />
                              )
                            })}
                          </VBox>
                        )
                      })
                    )}
                  </VBox>
                )}
              </Box>
            </Fragment>
          )}
          {!locationMismatch && fewerRepsThanExpected && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 1,
                borderRadius: '9px',
                background: theme => theme.palette.error.light
              }}
            >
              <WarningOutlined />
              <Typography color="textPrimary">
                We didn't find all the representatives for your area that we were hoping for. This
                can happen if your address isn't detailed enough. Consider filling out your address
                again so we can find all the people we want you to reach out to.
              </Typography>
            </Box>
          )}
        </Fragment>
      )}
      {targetsOther && targetsOther.length > 0 && (
        <Box>
          {!targetsAreOnlyOther && (
            <Box my={1}>
              <Typography variant="body1" color="textPrimary">
                Other folks you can contact:
              </Typography>
            </Box>
          )}
          <VBox gap={1}>
            {targetsOther.map(otherTarget => {
              // @ts-expect-error: existing type issue
              const { name, title, photoUrl } = otherTarget

              return (
                <RepresentativeButton
                  name={name}
                  title={title}
                  photo={photoUrl}
                  onClick={() => {
                    onSelectRep(otherTarget)
                  }}
                />
              )
            })}
          </VBox>
        </Box>
      )}
    </VBox>
  )
}

const getLocationMismatch = (
  location?: Queries.SanityLocation,
  locationData?: LocationResponse
) => {
  const missingCities = isMissingLocation(location?.cities as string[], locationData?.city)
    ? location?.cities || []
    : []
  const missingStates = isMissingLocation(location?.states as string[], locationData?.state)
    ? location?.states || []
    : []
  const missingCountries = isMissingLocation(location?.countries as string[], locationData?.country)
    ? location?.countries || []
    : []

  const combined = [...missingCities, ...missingStates, ...missingCountries].filter(
    exists => !!exists
  )
  return combined.length > 0 && addSentenceStructureToStringArray(combined, 'or')
}

const isMissingLocation = (locations?: string[], address?: AddressComponent) => {
  const noLocations = !locations || locations.length === 0
  if (noLocations) {
    return false
  }
  const locationSlugs = [...locations].map(location => slugify(location))
  const addressSlugs =
    address && [slugify(address.short_name), slugify(address.long_name)].filter(exists => !!exists)

  return !locationSlugs.find(slug =>
    addressSlugs.find(addressSlug => slug.indexOf(addressSlug) > -1)
  )
}

export default Representatives
